import { useEffect, useState } from "react";
import { Col, Row, Table } from "reactstrap";
import ErrorScreen from "../../error/ErrorScreen";
import getDataFromApi from "../../getDataFromApi";
import Loader from "../../Loader";
import "./DatConfiguration2.scss";

const DATASET_REVIEW_ORDER = [
  "Dataset Name",
  "Add Data",
  "Add Labels",
  "Object Details",
  "Labels",
  "labels",
  "Select Data Source",
  "Source",
  "Select Project",
  "Select Dataset",
  "Registered Devices",
  "Select Stream Id",
  "Network Path",
  "Bucket Type",
  "Bucket Name",
  "Folder Path",
  "Access Key",
  "Secret Key",
  "Content",
  "Image Extensions",
  "Video Extensions",
  "Extraction Frame Rate",
  "Directory Format",
  "Import/Load Labels",
  "Map to New Labels",
  "Avoid Similar Images",
  "Similarity Threshold",
  "Use AI to Annotate",
  "Video for Inference",
  "Upload Annotation Guidelines",
  "Upload Annotation Guidelines Name",
  "annotation_guideline_filename",
  "Task Name",
  "AI Assist",
  "Experiment Details",
  "Model Details",
  "Foundational Model Details",
  "Prompt Labels",
  "LABELS_PROMPT_KEY",
  "Score Threshold",
  "Label Map",
  "detailed_labels",
];

const INTERNAL_DATASET_REVIEW_KEYS = new Set([
  "Upload Data",
  "access_key",
  "secret_key",
  "session_token",
  "bucket_name",
  "folder_name",
  "model_name",
  "exp_name",
  "Project",
  "Dataset",
]);

const AI_RELATED_REVIEW_KEYS = new Set([
  "Task Name",
  "AI Assist",
  "Model Details",
  "Foundational Model Details",
  "Experiment Details",
  "Score Threshold",
  "Label Map",
  "LABELS_PROMPT_KEY",
  "Prompt Labels",
  "Images Per Task",
  "Images Pertask",
  "Tasks Per Taskset",
  "Taskset Group Name",
  "Task Group Name",
  "Task group name",
]);

const AUGMENTATION_REVIEW_SECTIONS = new Set([
  "Apply Augmentations as",
  "GaussianNoise",
  "Affine",
  "HorizontalFlip",
  "VerticalFlip",
  "RandomCrop",
  "RandomRotate90",
  "SafeRotate",
  "HueSaturationValue",
  "MultiplicativeNoise",
  "InvertImg",
  "ToGray",
  "RandomContrast",
  "RandomGamma",
  "RandomBrightnessContrast",
  "RandomBrightness",
  "ColorJitter",
  "ChannelShuffle",
]);

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeReviewKey = (key = "") =>
  String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const GUIDELINE_UPLOAD_KEYS = new Set([
  "uploadannotationguidelines",
  "uploadannotationguidelinesname",
  "uploadannotationguideline",
  "uploadannotationguidelinesfile",
  "uploadannotationguidelinefile",
  "uploadannotationguidelinesfilename",
  "annotationguidelinefilename",
  "annotationguidelinesfilename",
]);

const humanizeKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (char) => char.toUpperCase());

const isYesValue = (value) => {
  if (value === true) return true;

  return ["yes", "true", "1"].includes(String(value).trim().toLowerCase());
};

const isNoValue = (value) => {
  if (value === false) return true;

  return ["no", "false", "0", "none", "disabled"].includes(
    String(value ?? "").trim().toLowerCase()
  );
};

const isDataPayload = (value) =>
  typeof value === "string" &&
  (
    value.trim().startsWith("data:") ||
    /^[A-Za-z0-9+/]+={0,2}$/.test(value.trim()) && value.trim().length > 300
  );

const getGuidelineFilename = (configuration = {}) => {
  const filename =
    configuration["Upload Annotation Guidelines Name"] ||
    configuration.annotation_guideline_filename ||
    configuration.annotation_guidelines_filename;

  if (filename && !isDataPayload(filename)) return filename;

  const uploadValue =
    configuration["Upload Annotation Guidelines"] ||
    configuration.upload_annotation_guidelines;

  return uploadValue && !isDataPayload(uploadValue) ? uploadValue : "";
};

const prepareReviewConfiguration = (configuration = {}) => {
  if (!isPlainObject(configuration)) return configuration;

  const guidelineFilename = getGuidelineFilename(configuration);

  return Object.entries(configuration).reduce((acc, [key, value]) => {
    const normalizedKey = normalizeReviewKey(key);

    if (GUIDELINE_UPLOAD_KEYS.has(normalizedKey) || isDataPayload(value)) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, guidelineFilename ? { "Upload Annotation Guidelines Name": guidelineFilename } : {});
};

const formatLabelText = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (isPlainObject(item)) {
          return (
            item.name ||
            item.Name ||
            item.label ||
            item.Label ||
            item["Label Name"] ||
            formatDatasetReviewValue(item)
          );
        }

        return String(item).trim();
      })
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean).join(", ");
  }

  return formatDatasetReviewValue(value);
};

const formatDatasetReviewValue = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";

    return value
      .map((item) => (isPlainObject(item) ? formatDatasetReviewValue(item) : String(item).trim()))
      .filter(Boolean)
      .join(", ");
  }

  if (isPlainObject(value)) {
    if (value.apply === false) return null;

    return Object.entries(value)
      .filter(([key]) => key !== "apply" && !String(key).startsWith("__"))
      .map(([key, nestedValue]) => `${humanizeKey(key)}: ${formatDatasetReviewValue(nestedValue)}`)
      .filter((item) => !item.endsWith(": null"))
      .join(", ");
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value === null || value === undefined || value === "") {
    return "None";
  }

  const normalizedValue = String(value).replace(/\s+/g, " ").trim();

  if (normalizedValue === "true" || normalizedValue === "false") {
    return normalizedValue === "true" ? "Yes" : "No";
  }

  return normalizedValue;
};

const maskSecret = (value) => {
  const secret = String(value || "");

  if (secret.length <= 4) return "****";

  return `${"*".repeat(secret.length - 4)}${secret.slice(-4)}`;
};

const buildDatasetReviewRows = (configuration = {}) => {
  const selectedSource = configuration["Select Data Source"] || configuration.Source;
  const contentType = configuration.Content;
  const aiAnnotateEnabled = isYesValue(configuration["Use AI to Annotate"]);
  const selectedLabelsMode = configuration["Add Labels"];

  const getOrderIndex = (key) => {
    const index = DATASET_REVIEW_ORDER.indexOf(key);
    return index === -1 ? DATASET_REVIEW_ORDER.length : index;
  };

  const rows = [];
  const usedLabels = new Set();

  const addRow = (key, label, value, options = {}) => {
    const displayValue = typeof value === "string" ? value : formatDatasetReviewValue(value);
    const normalizedLabel = normalizeReviewKey(label);

    if (!displayValue) return;
    if (normalizedLabel && usedLabels.has(normalizedLabel)) return;
    if (normalizedLabel) usedLabels.add(normalizedLabel);

    rows.push({
      key,
      label,
      value: displayValue,
      scrollable: Boolean(options.scrollable),
      order: getOrderIndex(key),
    });
  };

  Object.entries(configuration)
    .sort(([keyA], [keyB]) => getOrderIndex(keyA) - getOrderIndex(keyB))
    .forEach(([key, value]) => {
      if (INTERNAL_DATASET_REVIEW_KEYS.has(key)) return;
      if (key === "Source" && configuration["Select Data Source"]) return;
      if (!aiAnnotateEnabled && AI_RELATED_REVIEW_KEYS.has(key)) return;
      if (isDataPayload(value)) return;
      if (GUIDELINE_UPLOAD_KEYS.has(normalizeReviewKey(key))) {
        addRow(key, "Upload Annotation Guidelines", value);
        return;
      }

      if (key === "Network Path" && selectedSource !== "LocalTransfer") return;
      if (key === "Select Project" && selectedSource !== "PlatformImport") return;
      if (key === "Select Dataset" && selectedSource !== "PlatformImport") return;
      if (key === "Registered Devices" && selectedSource !== "Registered_Device") return;
      if (key === "Select Stream Id" && selectedSource !== "Registered_Device") return;
      if (["Bucket Name", "Bucket Type", "Access Key", "Secret Key", "Folder Path"].includes(key) && selectedSource !== "CloudS3") return;

      if (key === "Image Extensions" && contentType !== "ImagesOnly") return;
      if (key === "Video Extensions" && contentType !== "VideosOnly") return;
      if (key === "Extraction Frame Rate" && contentType !== "VideosOnly") return;
      if (["Directory Format", "Import/Load Labels", "Map to New Labels"].includes(key) && contentType !== "Dataset") return;
      if (key === "Similarity Threshold" && !isYesValue(configuration["Avoid Similar Images"])) return;
      if (key === "Object Details" && selectedLabelsMode && selectedLabelsMode !== "Comma-separated values") return;
      if ((key === "Labels" || key === "labels") && selectedLabelsMode && selectedLabelsMode !== "Comma-separated values") return;
      if (key === "detailed_labels" && selectedLabelsMode && selectedLabelsMode !== "Detailed array") return;

      if (key === "Add Labels") {
        addRow(key, "Labels Provided as", value);
        return;
      }

      if (key === "Select Data Source" || key === "Source") {
        addRow(key, "Data Source", value);
        return;
      }

      if (key === "Access Key" || key === "Secret Key") {
        addRow(key, key, maskSecret(value));
        return;
      }

      if (key === "Object Details" && isPlainObject(value)) {
        if (value.labels) {
          addRow(key, "Labels", formatLabelText(value.labels), { scrollable: true });
        }

        if (Array.isArray(value.detailed_labels) && value.detailed_labels.length > 0) {
          addRow("detailed_labels", "Detailed Labels", formatDatasetReviewValue(value.detailed_labels));
        }

        return;
      }

      if ((key === "Labels" || key === "labels") && value) {
        addRow(key, "Labels", formatLabelText(value), { scrollable: true });
        return;
      }

      if (key === "Foundational Model Details" && isPlainObject(value)) {
        if (configuration["AI Assist"] !== "Use Foundational Model") return;

        addRow(key, "Foundational Models", configuration.model_name || value["Model Name"] || "-");

        if (value["LABELS_PROMPT_KEY"]) {
          addRow("LABELS_PROMPT_KEY", "Prompt Labels", formatLabelText(value["LABELS_PROMPT_KEY"]), { scrollable: true });
        }

        return;
      }

      if (key === "Model Details" && isPlainObject(value)) {
        if (configuration["AI Assist"] !== "Use Model") return;

        addRow(key, "Model Details", configuration.model_name || value["Model Name"] || "-");
        return;
      }

      if (key === "Experiment Details" && isPlainObject(value)) {
        if (configuration["AI Assist"] !== "Use Platform Experiment") return;

        addRow(key, "Experiment Details", configuration.exp_name || value["Experiment Name"] || "-");
        return;
      }

      if (key === "LABELS_PROMPT_KEY" || key === "Prompt Labels") {
        addRow(key, "Prompt Labels", formatLabelText(value), { scrollable: true });
        return;
      }

      if (key === "detailed_labels" && Array.isArray(value)) {
        addRow(key, "Detailed Labels", formatDatasetReviewValue(value));
        return;
      }

      if (key === "Map to New Labels" && Array.isArray(value)) {
        const labelMap = value
          .map((item) => {
            const sourceLabel = Array.isArray(item["Source Label"])
              ? item["Source Label"].join(", ")
              : item["Source Label"];
            return `${sourceLabel} -> ${item["Target Label"]}`;
          })
          .join("\n");

        addRow(key, key, labelMap);
        return;
      }

      if (key === "Label Map" && Array.isArray(value)) {
        const labelMap = value
          .map((item) => {
            const modelLabels = Array.isArray(item["Model Labels"])
              ? item["Model Labels"].join(", ")
              : item["Model Labels"];
            return `${modelLabels} -> ${item["Dataset Label"]}`;
          })
          .join("\n");

        addRow(key, key, labelMap);
        return;
      }

      addRow(key, key, value);
    });

  return rows.sort((rowA, rowB) => rowA.order - rowB.order);
};

export default function DataConfiguration2({ expData, query, id }) {
  const [data, setData] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const customOrder = [
    "Preprocessing",
    "Apply Augmentations",
    "Apply Augmentations as",
    "GaussianNoise",
    "Affine",
    "HorizontalFlip",
    "VerticalFlip",
    "RandomCrop",
    "RandomRotate90",
    "SafeRotate",
    "HueSaturationValue",
    "MultiplicativeNoise",
    "InvertImg",
    "ToGray",
    "RandomContrast",
    "RandomGamma",
    "RandomBrightnessContrast",
    "RandomBrightness",
    "ColorJitter",
    "ChannelShuffle",
    "Filter By"
  ];

  useEffect(() => {
    setLoading(true);
    if (!query) {
      setData(expData);
      setLoading(false);
      return;
    }
    const url = `${window['ENV'].API_URL}/models/detailed/${id}/configuration${query}`;
    getDataFromApi(url, "get")
      .then((r) => {
        if (r?.detail) alert(r.detail);
        else {
          setData(r);
          setLoading(false);
        }
      })
      .catch((e) => {
        setError(e.message);
        console.log(e);
      });
    return () => {
      setError();
    };
  }, [id, query, expData]);

  if (loading) return <Loader />;
  if (error || !data) return <ErrorScreen message={"No Data Found"} />;

  const formatValue = (key, value) => {
    if (Array.isArray(value)) {
      return value.length === 0 ? "None" : value.join(", ");
    } else if (typeof value === 'object' && value !== null) {
      if (value.apply === false) return null;

      return Object.entries(value)
        .filter(([k]) => k !== 'apply')
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
        .join(", ");
    } else if (value === null || value === '') {
      return "None";
    }
    return String(value).replace(/\s+/g, ' ').trim(); // Ensure values are on a single line
  };


  const renderTable = (obj) => {
    return (
      <Table className="dataconfig_table dataconfig-review__table">
        <tbody>
          {Object.entries(obj).map(([key, value]) => {

            if (typeof value === 'object' && value !== null && value.apply === false) {
              return null;
            }
            let displayValue = '';
            if (key === 'min_image_height' && value.min_height !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_height}</td>
                </tr>
              );
            }

            if (key === 'max_image_height' && value.max_height !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.max_height}</td>
                </tr>
              );
            }

            if (key === 'min_image_width' && value.min_width !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_width}</td>
                </tr>
              );
            }

            if (key === 'max_image_width' && value.max_width !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.max_width}</td>
                </tr>
              );
            }

            if (key === 'max_image_size' && value.max_size !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.max_size}</td>
                </tr>
              );
            }

            if (key === 'min_image_brightness' && value.min_brightness !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_brightness}</td>
                </tr>
              );
            }

            if (key === 'min_num_of_objects' && value.min_objects !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_objects}</td>
                </tr>
              );
            }
            if (key === 'min_bbox_height' && value.min_bbox_height !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_bbox_height}</td>
                </tr>
              );
            }
            if (key === 'max_bbox_height' && value.max_bbox_height !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.max_bbox_height}</td>
                </tr>
              );
            }
            if (key === 'min_bbox_width' && value.min_bbox_width !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_bbox_width}</td>
                </tr>
              );
            }
            if (key === 'max_bbox_width' && value.max_bbox_width !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.max_bbox_width}</td>
                </tr>
              );
            }
            if (key === 'min_bbox_area' && value.min_bbox_area !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_bbox_area}</td>
                </tr>
              );
            }
            if (key === 'min_num_of_objects' && value.min_objects !== undefined) {

              return (
                <tr key={key} style={{ borderBottom: 'none' }}>
                  <td className="table-cell1">
                    {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  </td>
                  <td className="table-cell1">{value.min_objects}</td>
                </tr>
              );
            }



            if (typeof value === 'object' && 'apply' in value) {

              displayValue = Object.entries(value)
                .filter(([nestedKey]) => nestedKey !== 'apply')
                .map(([nestedKey, nestedValue]) => `${nestedKey}: ${nestedValue}`)
                .join(', ') || 'Apply: True';
            } else {

              displayValue = formatValue(key, value);
            }


            if (!displayValue) return null;
            if (displayValue === "true" || displayValue === "false") {
              displayValue = displayValue === "true" ? "Yes" : "No";
            } else if (displayValue === "Apply: True") {
              displayValue = "Yes"
            }


            if (key === "missing_annotations") {
              key = "Unannotated Images"
            }
            if (key === "only_annotations") {
              key = "Annotated Images"
            }
            if(key === "filter_images_not_inspected"){
              key = "Image Inspection"
            }


            return (
              <tr key={key} style={{ borderBottom: 'none' }}>
                <td className="table-cell1 dataconfig-review__cell">
                  {key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                </td>
                <td className="table-cell1 dataconfig-review__cell dataconfig-review__value">{displayValue}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const renderFlatReview = (configuration) => {
    const rows = buildDatasetReviewRows(configuration);

    return (
      <div className="my-4 mt-0 dataconfig-review__section dataset-review">
        {/* <h5 className="dataset-review__heading">Review</h5> */}
        <hr className="dataset-review__divider" />
        <div className="p-2 pt-4 pb-1 collapse-child grey-card">
          <Row>
            <Col sm={12}>
              {rows.length > 0 ? (
                <Table className="dataconfig_table dataconfig-review__table dataset-review__table">
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.key}-${row.label}`} style={{ borderBottom: 'none' }}>
                        <td className="table-cell1 dataconfig-review__cell dataset-review__label">
                          {row.label}
                        </td>
                        <td className="table-cell1 dataconfig-review__cell dataconfig-review__value dataset-review__value">
                          {row.scrollable ? (
                            <div className="dataset-review__scroll-value">
                              {row.value}
                            </div>
                          ) : row.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="dataconfig-review__inline-value">No configuration data available.</p>
              )}
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  const configuration = prepareReviewConfiguration(data?.configuration);
  const sortedSections = customOrder.filter(section => isPlainObject(configuration?.[section]));

  if (!isPlainObject(configuration)) {
    return <ErrorScreen message={"No configuration data available."} />;
  }

  const applyAugmentationsValue = configuration["Apply Augmentations"];
  const shouldShowAugmentationDetails = !isNoValue(applyAugmentationsValue);
  const hasApplyAugmentationsValue =
    applyAugmentationsValue !== undefined &&
    applyAugmentationsValue !== null &&
    applyAugmentationsValue !== "";
  const hasApplyAugmentationsAsValue =
    configuration["Apply Augmentations as"] &&
    configuration["Apply Augmentations as"] !== "";

  if (sortedSections.length === 0) {
    return renderFlatReview(configuration);
  }


  return (
    <>
      {sortedSections.map((section) => {
        if (!shouldShowAugmentationDetails && AUGMENTATION_REVIEW_SECTIONS.has(section)) return null;

        if (section === "Apply Augmentations" && hasApplyAugmentationsAsValue && shouldShowAugmentationDetails) return null;
       
        if (section && configuration[section].apply === false) return null;

        if (section === "Apply Augmentations") return null;
        if (section === "Apply Augmentations as") return null;

        const sectionData = configuration[section];
        if (!sectionData || Object.keys(sectionData).length === 0) return null;


        const filteredSectionData = Object.entries(sectionData).reduce((acc, [key, value]) => {

          if (typeof value === 'object' && value.apply === false) {
            return acc;
          }
          acc[key] = value;
          return acc;
        }, {});


        if (Object.keys(filteredSectionData).length === 0) return null;

        return (
          <div className="my-4 mt-0 dataconfig-review__section" key={section}>
            <h6 className="p-2 ps-1 dataconfig-review__title">{section}</h6>
            <div className="p-2 pt-4 pb-1 collapse-child grey-card">
              <Row>
                <Col sm={12}>
                  {renderTable(filteredSectionData)}
                </Col>
              </Row>
            </div>

            {/* Insert "Apply Augmentations as" directly after "Preprocessing" */}
            {(section === "Preprocessing" && hasApplyAugmentationsAsValue && shouldShowAugmentationDetails) && (
              <div className="my-4 mt-0">
                <h6 className="p-2 ps-1 dataconfig-review__title">Apply Augmentations as</h6>
                <div className="p-2 collapse-child grey-card dataconfig-review__single-value-card">
                  <Row>
                    <Col sm={12}>
                      <p className="table-cell1 dataconfig-review__inline-value">
                        {configuration["Apply Augmentations as"]}
                      </p>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
            {(section === "Preprocessing" && hasApplyAugmentationsValue && !shouldShowAugmentationDetails) && (
              <div className="my-4 mt-0">
                <h6 className="p-2 ps-1 dataconfig-review__title">Apply Augmentations</h6>
                <div className="p-2 collapse-child grey-card dataconfig-review__single-value-card">
                  <Row>
                    <Col sm={12}>
                      <p className="table-cell1 dataconfig-review__inline-value">
                        {configuration["Apply Augmentations"]}
                      </p>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );



}
